import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useId, useState } from 'react';
import styled, { css } from 'styled-components';
import { colorBlack, colorWhite, colorBrandBase, colorLightest, colorDarkest, colorDarker, copyFontFamily, copyFontSizeSmall, copyLineHeight, focusOutlineWidth, focusOutlineStyle, token, focusOutlineColor } from '@nib-components/theme';
import { Inline, Stack, Columns, Column, Hidden, Container, breakpoint, p, pb, px, py, m, mr } from '@nib/layout';
import Link from '@nib-components/link';
import { EmailSystemIcon, LocationSystemIcon, PhoneSystemIcon, ChevronDownSystemIcon } from '@nib/icons';
import { SmallFooter } from '../SmallFooter';
import Expander from '@nib-components/expander';
import Heading from '@nib-components/heading';
import configByVariation from '../config';
const FooterWrapper = styled.footer`
  font-family: ${copyFontFamily};
  font-size: ${copyFontSizeSmall};
  ${_ref => {
  let {
    theme
  } = _ref;
  return (theme.id === 'nib' || theme.id === 'foundation') && css`
      --text-color: var(--footerColorFg, ${colorWhite});
      --background-color: var(--footerColorBg, ${colorBrandBase});
      --icon-hover-color: hsl(0deg 0% 0% / 42%);
      &&& :focus-visible {
        outline-color: ${token('footer.color.border.focus', '#b9b5d3')};
      }
    `;
}}
  color: var(--footerColorFg, ${colorLightest});
  background-color: var(--footerColorBg, ${colorDarkest});
`;
const FooterContents = styled.div`
  ${p({
  xs: 4,
  sm: 5
})};
  ${breakpoint('xs', 'md')`
    ${pb(0)};
  `};
`;
const WrappingHeading = styled.h3`
  margin: 0;
  padding: 0;
`;
const FooterHeading = styled(props => /*#__PURE__*/_jsx(Heading, {
  component: "h3",
  size: 6,
  ...props
}))`
  min-height: 1.5rem;
  color: var(--footerColorFgHeading, inherit);
`;
const FooterLinkColumns = styled(Columns)`
  ${breakpoint('xs', 'md')`
    display: none;
  `};
`;
const FooterLinkExpanders = styled.ul`
  display: flex;
  flex-direction: column;
  ${m(0)};
  ${p(0)};
  list-style: none;

  ${breakpoint('md')`
    display: none;
  `};
`;
const LinkExpander = styled.button`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  border: none;
  color: var(--footerColorFgLink, ${colorLightest});
  background: none;
  padding: 0;
  ${px(4)};
  ${py(4)};
  line-height: ${copyLineHeight};

  &:focus-visible {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('footer.color.border.focus', focusOutlineColor)};
  }
`;
const ExpanderItem = styled.li`
  display: block;
  position: relative;
  border-block-start: 1px solid var(--footerColorBorder, ${colorDarker});

  &:last-of-type {
    border-block-end: 1px solid var(--footerColorBorder, ${colorDarker});
  }
`;
const ExpanderLink = styled(Link).attrs({
  underline: false
})`
  display: block;
  ${px(5)};
  ${py(4)};

  &&& {
    color: inherit;
  }

  &:focus-visible {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('footer.color.border.focus', focusOutlineColor)};
    outline-offset: -${focusOutlineWidth};
  }
`;
// eslint-disable-next-line  @typescript-eslint/no-unused-vars
const Chevron = styled(_ref2 => {
  let {
    expanded,
    ...otherProps
  } = _ref2;
  return /*#__PURE__*/_jsx(ChevronDownSystemIcon, {
    ...otherProps
  });
})`
  ${_ref3 => {
  let {
    expanded
  } = _ref3;
  return expanded && `transform: rotate(180deg);`;
}};
  transition: transform 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
`;
const ExpanderContent = styled.ul`
  ${m(0)};
  ${p(0)};
  list-style: none;
`;
const PhoneHeading = styled(FooterHeading)`
  display: inline-block;
  font-size: 1.5rem;
  color: var(--text-color, inherit);
`;
export const InternationalPhoneHeading = styled(FooterHeading).attrs({
  'data-testid': 'international-phone-heading'
})`
  font-size: ${copyFontSizeSmall};
`;
const ContactLink = styled(Link).attrs({
  underline: false
})`
  display: inline-block;

  &&& {
    color: var(--footerColorFgLink, inherit);
    white-space: normal;
  }

  &&&:hover {
    color: var(--footerColorFgLinkHover);
    text-decoration: underline;
  }

  &&&:focus-visible {
    color: var(--footerColorFgLinkHover);
    text-decoration: underline;
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('footer.color.border.focus', focusOutlineColor)};
  }
`;
const PhoneContactLink = styled(ContactLink)`
  &:focus-visible {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('footer.color.border.focus', focusOutlineColor)};
    outline-offset: 0;
  }
`;
const FooterLink = styled(Link)`
  &&& {
    color: var(--footerColorFgLink, inherit);
    white-space: normal;
  }

  &&&:hover {
    color: var(--footerColorFgLinkHover);
  }

  &&&:focus-visible {
    color: var(--footerColorFgLinkHover);
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('footer.color.border.focus', focusOutlineColor)};
  }
`;
const FooterIcon = styled(_ref4 => {
  let {
    icon: Icon,
    ...rest
  } = _ref4;
  return /*#__PURE__*/_jsx(Icon, {
    size: "xs",
    ...rest
  });
})`
  vertical-align: sub;
`;
const PhoneNumberSpan = styled.span`
  unicode-bidi: plaintext;
`;
const SocialIcon = styled.a`
  ${mr(1)};
  display: inline-block;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-position: 8px 8px;
  background-size: 16px;
  background-repeat: no-repeat;
  border: 1px solid var(--text-color, ${colorLightest});
  background-color: var(--background-color, ${colorDarkest});

  &:focus-visible {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${token('footer.color.border.focus', focusOutlineColor)};
  }
`;
const SocialMediaLink = props => /*#__PURE__*/_jsx(SocialIcon, {
  ...props,
  children: /*#__PURE__*/_jsx(Hidden, {
    screen: true,
    children: /*#__PURE__*/_jsx("span", {
      children: props.label
    })
  })
});
const IconStateStyles = () => css`
  background-size: 16px 16px;
  background-position: center;

  &:hover,
  &:focus-visible {
    background-color: var(--icon-hover-color, ${colorBlack});
  }
`;
const FacebookIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCI+PHBhdGggZmlsbD0iI0ZGRiIgZD0iTTM3LjEgNjRWMzQuOGg5LjhsMS41LTExLjRIMzcuMXYtNy4zYzAtMy4zLjktNS41IDUuNi01LjVoNlYuNEM0Ny43LjMgNDQuMiAwIDQwIDBjLTguNyAwLTE0LjYgNS4zLTE0LjYgMTV2OC40aC05Ljh2MTEuNGg5LjhWNjRoMTEuN3oiLz48L3N2Zz4=');
    ${IconStateStyles};
  }
`;
const TwitterIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIKCSB3aWR0aD0iNjRweCIgaGVpZ2h0PSI2NHB4IiB2aWV3Qm94PSIwIDAgNjQgNjQiIGVuYWJsZS1iYWNrZ3JvdW5kPSJuZXcgMCAwIDY0IDY0IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHBhdGggZmlsbD0iI0ZGRkZGRiIgZD0iTTY0LDEyLjJjLTIuNCwxLTQuOSwxLjgtNy41LDIuMWMyLjctMS42LDQuOC00LjIsNS44LTcuM0M1OS44LDguNSw1Nyw5LjYsNTQsMTAuMkM1MS41LDcuNiw0OC4xLDYsNDQuMyw2CglDMzcsNiwzMS4yLDExLjksMzEuMiwxOS4xYzAsMSwwLjEsMiwwLjMsM0MyMC42LDIxLjYsMTEsMTYuNCw0LjUsOC40Yy0xLjEsMS45LTEuOCw0LjItMS44LDYuNmMwLDQuNiwyLjMsOC42LDUuOCwxMC45CgljLTIuMi0wLjEtNC4yLTAuNy01LjktMS42YzAsMC4xLDAsMC4xLDAsMC4yYzAsNi40LDQuNSwxMS43LDEwLjUsMTIuOWMtMS4xLDAuMy0yLjMsMC41LTMuNSwwLjVjLTAuOCwwLTEuNy0wLjEtMi41LTAuMgoJYzEuNyw1LjIsNi41LDksMTIuMyw5LjFDMTUsNTAuMiw5LjMsNTIuMywzLjIsNTIuM2MtMS4xLDAtMi4xLTAuMS0zLjEtMC4yQzUuOCw1NS45LDEyLjgsNTgsMjAuMiw1OGMyNC4yLDAsMzcuNC0yMCwzNy40LTM3LjQKCWMwLTAuNiwwLTEuMSwwLTEuN0M2MC4xLDE3LjEsNjIuMywxNC44LDY0LDEyLjJ6Ii8+Cjwvc3ZnPg==');
    ${IconStateStyles};
  }
`;
const YoutubeIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIKCSB3aWR0aD0iNjRweCIgaGVpZ2h0PSI2NHB4IiB2aWV3Qm94PSIwIDAgNjQgNjQiIGVuYWJsZS1iYWNrZ3JvdW5kPSJuZXcgMCAwIDY0IDY0IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPGc+Cgk8cGF0aCBmaWxsPSIjRkZGRkZGIiBkPSJNNjMuNCw0NC44YzAsMC0wLjYsNC40LTIuNSw2LjRjLTIuNCwyLjUtNS4yLDIuNi02LjQsMi43Yy05LDAuNi0yMi40LDAuNy0yMi40LDAuN1MxNS41LDU0LjQsMTAuMyw1NAoJCWMtMS40LTAuMy00LjYtMC4yLTcuMS0yLjdjLTEuOS0xLjktMi41LTYuNC0yLjUtNi40UzAsMzkuNiwwLDM0LjR2LTQuOWMwLTUuMiwwLjYtMTAuNCwwLjYtMTAuNHMwLjYtNC40LDIuNS02LjQKCQljMi40LTIuNSw1LjItMi42LDYuNC0yLjdDMTguNiw5LjUsMzIsOS41LDMyLDkuNWwwLDBjMCwwLDEzLjQsMCwyMi40LDAuNmMxLjMsMC4xLDQsMC4yLDYuNCwyLjdjMS45LDEuOSwyLjUsNi40LDIuNSw2LjQKCQlTNjQsMjQuNCw2NCwyOS42djQuOUM2NCwzOS42LDYzLjQsNDQuOCw2My40LDQ0Ljh6IE0yNS40LDIyLjN2MThsMTcuMi05TDI1LjQsMjIuM3oiLz4KPC9nPgo8L3N2Zz4=');
    ${IconStateStyles};
  }
`;
const InstagramIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,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');
    ${IconStateStyles};
  }
`;
const LinkedInIcon = styled(SocialMediaLink)`
  && {
    background-image: url('data:image/svg+xml;base64,PHN2ZyBpZD0nTGF5ZXJfMScgeG1sbnM9J2h0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnJyB2aWV3Qm94PScwIDAgMjUuOCAyNS43Jz48c3R5bGU+LnN0MHtmaWxsOiNGRkZGRkY7fTwvc3R5bGU+PHBhdGggY2xhc3M9J3N0MCcgZD0nTTUuOCAyNS43SC40VjguNWg1LjN2MTcuMnpNMy4xIDYuMkMxLjQgNi4yIDAgNC44IDAgMy4xIDAgMS40IDEuNCAwIDMuMSAwYzEuNyAwIDMuMSAxLjQgMy4xIDMuMSAwIDEuNy0xLjQgMy4xLTMuMSAzLjF6bTIyLjcgMTkuNWgtNS4zdi04LjRjMC0yIDAtNC42LTIuOC00LjZzLTMuMiAyLjItMy4yIDQuNHY4LjVIOS4xVjguNWg1LjF2Mi4zaC4xQzE1IDkuNCAxNi44IDggMTkuNCA4YzUuNCAwIDYuNCAzLjYgNi40IDguMnY5LjV6Jy8+PC9zdmc+');
    ${IconStateStyles};
  }
`;
const FooterExpander = _ref5 => {
  let {
    heading,
    children
  } = _ref5;
  const [isExpanded, setExpanded] = useState(false);
  const expanderId = useId();
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(WrappingHeading, {
      children: /*#__PURE__*/_jsxs(LinkExpander, {
        title: heading,
        "aria-expanded": isExpanded,
        "aria-controls": expanderId,
        onClick: () => setExpanded(!isExpanded),
        children: [/*#__PURE__*/_jsx(FooterHeading, {
          component: "div",
          children: heading
        }), /*#__PURE__*/_jsx(Chevron, {
          size: "xs",
          expanded: isExpanded
        })]
      })
    }), /*#__PURE__*/_jsx(Expander, {
      expanded: isExpanded,
      "aria-label": heading,
      id: expanderId,
      children: /*#__PURE__*/_jsx(ExpanderContent, {
        children: children
      })
    })]
  });
};
FooterExpander.displayName = 'FooterExpander';
const SocialMediaIconLink = _ref6 => {
  let {
    link
  } = _ref6;
  const {
    title,
    id,
    url,
    key
  } = link;
  switch (key) {
    case 'Facebook':
      return /*#__PURE__*/_jsx(FacebookIcon, {
        component: `${key}Icon`,
        id: id,
        href: url,
        title: title,
        label: key,
        rel: "nofollow"
      }, id);
    case 'Twitter':
      return /*#__PURE__*/_jsx(TwitterIcon, {
        component: `${key}Icon`,
        id: id,
        href: url,
        title: title,
        label: key,
        rel: "nofollow"
      }, id);
    case 'Youtube':
      return /*#__PURE__*/_jsx(YoutubeIcon, {
        component: `${key}Icon`,
        id: id,
        href: url,
        title: title,
        label: key,
        rel: "nofollow"
      }, id);
    case 'Instagram':
      return /*#__PURE__*/_jsx(InstagramIcon, {
        component: `${key}Icon`,
        id: id,
        href: url,
        title: title,
        label: key,
        rel: "nofollow"
      }, id);
    case 'LinkedIn':
      return /*#__PURE__*/_jsx(LinkedInIcon, {
        component: `${key}Icon`,
        id: id,
        href: url,
        title: title,
        label: key,
        rel: "nofollow"
      }, id);
    default:
      return /*#__PURE__*/_jsx(SocialIcon, {
        component: `${key}Icon`,
        id: id,
        href: url,
        title: title,
        label: key,
        rel: "nofollow"
      }, id);
  }
};
const PhoneHeadings = _ref7 => {
  let {
    countryOfOrigin,
    contact,
    defaultOrCorporatePhoneNumber
  } = _ref7;
  return /*#__PURE__*/_jsx(PhoneHeading, {
    children: /*#__PURE__*/_jsxs(Stack, {
      space: 4,
      children: [contact.phoneNumber && defaultOrCorporatePhoneNumber && /*#__PURE__*/_jsxs(Stack, {
        space: 2,
        children: [contact.overseasPhoneNumber && /*#__PURE__*/_jsxs(InternationalPhoneHeading, {
          children: ["From ", countryOfOrigin, ":"]
        }), /*#__PURE__*/_jsx(PhoneContactLink, {
          id: "ga-footer-call-desktop",
          href: `tel:${defaultOrCorporatePhoneNumber.replace(/\s+/g, '')}`,
          "aria-label": `Call from ${countryOfOrigin}`,
          children: /*#__PURE__*/_jsxs(Inline, {
            space: 2,
            children: [/*#__PURE__*/_jsx(FooterIcon, {
              icon: PhoneSystemIcon,
              size: "sm"
            }), /*#__PURE__*/_jsx(PhoneNumberSpan, {
              children: /*#__PURE__*/_jsx("strong", {
                children: defaultOrCorporatePhoneNumber
              })
            })]
          })
        })]
      }), contact.overseasPhoneNumber && /*#__PURE__*/_jsxs(Stack, {
        space: 2,
        children: [/*#__PURE__*/_jsx(InternationalPhoneHeading, {
          children: "From overseas:"
        }), /*#__PURE__*/_jsx(PhoneContactLink, {
          id: "ga-footer-call-overseas-desktop",
          href: `tel:${contact.overseasPhoneNumber.replace(/\s+/g, '')}`,
          "aria-label": "Call from overseas",
          children: /*#__PURE__*/_jsxs(Inline, {
            space: 2,
            children: [/*#__PURE__*/_jsx(FooterIcon, {
              icon: PhoneSystemIcon,
              size: "sm"
            }), /*#__PURE__*/_jsx(PhoneNumberSpan, {
              children: /*#__PURE__*/_jsx("strong", {
                children: contact.overseasPhoneNumber
              })
            })]
          })
        })]
      })]
    })
  });
};
export const Footer = props => {
  const {
    config: customConfig,
    variation = 'arhi',
    nzShielded,
    isCorporate = false,
    withAbsoluteLinks = false,
    absoluteBaseUrl,
    containerWidth,
    forceHideAcknowledgementOfCountry = false,
    ...otherProps
  } = props;
  const config = customConfig !== null && customConfig !== void 0 ? customConfig : configByVariation[variation]({
    withAbsoluteLinks,
    absoluteBaseUrl
  });
  const {
    id,
    contact,
    countryOfOrigin = 'Australia',
    smallFooterConfig
  } = config;
  const defaultOrCorporatePhoneNumber = isCorporate && (contact === null || contact === void 0 ? void 0 : contact.corporatePhoneNumber) ? contact.corporatePhoneNumber : contact === null || contact === void 0 ? void 0 : contact.phoneNumber;
  const phoneNumberLink = (contact === null || contact === void 0 ? void 0 : contact.contactUrl) || '/contact-us';
  return /*#__PURE__*/_jsxs(FooterWrapper, {
    "data-mesh-component": "FOOTER",
    id: id,
    ...otherProps,
    children: [/*#__PURE__*/_jsx(FooterContents, {
      children: /*#__PURE__*/_jsx(Container, {
        width: containerWidth,
        children: /*#__PURE__*/_jsxs(Columns, {
          space: {
            xs: 8,
            xl: 6
          },
          collapseBelow: "xl",
          children: [/*#__PURE__*/_jsx(Column, {
            children: /*#__PURE__*/_jsxs(Stack, {
              space: {
                xs: 8,
                xl: 6
              },
              children: [/*#__PURE__*/_jsxs(Stack, {
                space: 4,
                children: [(contact === null || contact === void 0 ? void 0 : contact.heading) && /*#__PURE__*/_jsx(FooterHeading, {
                  children: contact.heading
                }), contact && !contact.contactLinks && !contact.openHours && contact.phoneNumber && /*#__PURE__*/_jsx(PhoneHeadings, {
                  countryOfOrigin: countryOfOrigin,
                  contact: contact,
                  defaultOrCorporatePhoneNumber: defaultOrCorporatePhoneNumber
                }), (contact === null || contact === void 0 ? void 0 : contact.contactLinks) && /*#__PURE__*/_jsxs(Stack, {
                  space: 4,
                  children: [defaultOrCorporatePhoneNumber && /*#__PURE__*/_jsx(ContactLink, {
                    icon: PhoneSystemIcon,
                    iconPlacement: "left",
                    id: "ga-footer-contact-us-desktop",
                    href: phoneNumberLink,
                    children: "Contact us"
                  }), contact.contactLinks.map((link, i) => {
                    const Icon = link.icon;
                    return /*#__PURE__*/_jsx(ContactLink, {
                      icon: Icon,
                      iconPlacement: "left",
                      href: link.url,
                      target: link.targetBlank ? '_blank' : '',
                      id: `${link.id}-desktop`,
                      children: /*#__PURE__*/_jsx("span", {
                        children: link.title
                      })
                    }, `footer-link-${i}-${link.id}-desktop`);
                  })]
                }), (contact === null || contact === void 0 ? void 0 : contact.openHours) && /*#__PURE__*/_jsxs(Stack, {
                  space: 4,
                  children: [(contact.phoneNumber || contact.overseasPhoneNumber) && /*#__PURE__*/_jsx(PhoneHeadings, {
                    countryOfOrigin: countryOfOrigin,
                    contact: contact,
                    defaultOrCorporatePhoneNumber: defaultOrCorporatePhoneNumber
                  }), /*#__PURE__*/_jsx(Stack, {
                    space: 2,
                    children: contact.openHours.map((hours, i) => /*#__PURE__*/_jsx("div", {
                      children: hours
                    }, `footer-open-hours-${i}`))
                  })]
                }), (contact === null || contact === void 0 ? void 0 : contact.email) && (contact === null || contact === void 0 ? void 0 : contact.address) && /*#__PURE__*/_jsxs(Stack, {
                  space: 4,
                  children: [contact.email && /*#__PURE__*/_jsxs(Columns, {
                    space: 2,
                    children: [/*#__PURE__*/_jsx(Column, {
                      width: "content",
                      children: /*#__PURE__*/_jsx(FooterIcon, {
                        icon: EmailSystemIcon
                      })
                    }), /*#__PURE__*/_jsxs(Column, {
                      children: [/*#__PURE__*/_jsx(FooterHeading, {
                        children: "Email"
                      }), /*#__PURE__*/_jsx(ContactLink, {
                        href: `mailto:${contact.email}`,
                        children: contact.email
                      })]
                    })]
                  }), contact.address && /*#__PURE__*/_jsxs(Columns, {
                    space: 2,
                    children: [/*#__PURE__*/_jsx(Column, {
                      width: "content",
                      children: /*#__PURE__*/_jsx(FooterIcon, {
                        icon: LocationSystemIcon
                      })
                    }), /*#__PURE__*/_jsxs(Column, {
                      children: [/*#__PURE__*/_jsx(FooterHeading, {
                        children: "Address"
                      }), /*#__PURE__*/_jsxs(Stack, {
                        space: 2,
                        children: [/*#__PURE__*/_jsx("div", {
                          children: contact.address.mail.map((item, i) => /*#__PURE__*/_jsx("div", {
                            children: item
                          }, `footer-mail-address-${i}`))
                        }), /*#__PURE__*/_jsx("div", {
                          children: contact.address.office.map((item, i) => /*#__PURE__*/_jsx("div", {
                            children: item
                          }, `footer-office-address-${i}`))
                        })]
                      })]
                    })]
                  })]
                }), contact && contact.contactUrl && /*#__PURE__*/_jsx(FooterLink, {
                  id: "ga-footer-contact-us-desktop",
                  href: contact.contactUrl,
                  children: "Contact us"
                })]
              }), (contact === null || contact === void 0 ? void 0 : contact.socialLinks) && /*#__PURE__*/_jsxs(Stack, {
                space: 4,
                children: [/*#__PURE__*/_jsx(FooterHeading, {
                  children: "Stay connected"
                }), /*#__PURE__*/_jsxs(Inline, {
                  space: 2,
                  children: [contact.socialLinks.length > 0 && contact.socialLinks.map(link => /*#__PURE__*/_jsx(SocialMediaIconLink, {
                    link: link
                  }, link.id)), " ", nzShielded]
                })]
              }), (contact === null || contact === void 0 ? void 0 : contact.associations) && /*#__PURE__*/_jsxs(Stack, {
                space: 4,
                children: [/*#__PURE__*/_jsx(FooterHeading, {
                  children: "Associations"
                }), /*#__PURE__*/_jsx(Inline, {
                  space: 3,
                  collapseBelow: "sm",
                  children: contact.associations.length > 0 && contact.associations.map(link => {
                    return /*#__PURE__*/_jsx("img", {
                      src: link.src,
                      alt: link.alt || link.name,
                      height: "64",
                      loading: "lazy"
                    }, link.name);
                  })
                })]
              })]
            })
          }), config.columns && /*#__PURE__*/_jsx(Column, {
            width: "content",
            children: /*#__PURE__*/_jsx(FooterLinkColumns, {
              "data-testid": "footer-link-columns",
              space: {
                xs: 4,
                xl: 6
              },
              children: config.columns.map((column, i) => /*#__PURE__*/_jsx(Column, {
                children: /*#__PURE__*/_jsxs(Stack, {
                  space: 4,
                  children: [/*#__PURE__*/_jsx(FooterHeading, {
                    children: column.title
                  }), column.children.map((link, i) => /*#__PURE__*/_jsx(FooterLink, {
                    id: `${link.id}-desktop`,
                    href: link.url,
                    target: link.targetBlank ? '_blank' : '',
                    children: link.title
                  }, `footer-link-${i}-${link.id}-desktop`))]
                })
              }, `footer-links-column-${i}`))
            })
          })]
        })
      })
    }), config.columns !== undefined && /*#__PURE__*/_jsx(FooterLinkExpanders, {
      "data-testid": "footer-link-expanders",
      children: config.columns.map((column, i) => column.title && column.title !== ' ' && /*#__PURE__*/_jsx(ExpanderItem, {
        children: /*#__PURE__*/_jsxs(FooterExpander, {
          heading: column.title,
          children: [column.children.map((link, i) => /*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsx(ExpanderLink, {
              id: `${link.id}-mobile`,
              href: link.url,
              target: link.targetBlank ? '_blank' : '',
              children: link.title
            })
          }, `footer-link-${i}-${link.id}-mobile`)), config.columns[i + 1] && config.columns[i + 1].title === ' ' && config.columns[i + 1].children.map((link, i) => /*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsx(ExpanderLink, {
              id: `${link.id}-mobile`,
              href: link.url,
              target: link.targetBlank ? '_blank' : '',
              children: link.title
            })
          }, `footer-link-${i}-${link.id}-mobile`))]
        })
      }, `footer-link-expander-${i}-mobile`))
    }), /*#__PURE__*/_jsx(SmallFooter, {
      variation: variation,
      config: smallFooterConfig,
      isFooterAttachment: true,
      forceHideAcknowledgementOfCountry: forceHideAcknowledgementOfCountry,
      withAbsoluteLinks: withAbsoluteLinks,
      absoluteBaseUrl: absoluteBaseUrl,
      containerWidth: containerWidth
    })]
  });
};
Footer.displayName = 'Footer';
Footer.FooterWrapper = FooterWrapper;
Footer.FooterLinkColumns = FooterLinkColumns;
Footer.FooterLinkExpanders = FooterLinkExpanders;